import { ChatBlockDisclosure, type ChatBlockContext } from "./block-disclosure";
import { css, Style } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { AngleRightIcon, LonaIcons } from "@tento-ui/ui/icons";
import { Typography } from "@tento-ui/ui/typography";
import { ChatLoadingDots } from "./chat-loading-dots";
import { ChatTerminalLines } from "./chat-terminal-lines";
import { ChatContentBlock } from "./chat-types";
import { Renderers } from "../render/prose";
import type { ChatRenderers } from "../render/prose";
import { SessionReplay } from "../term/session-replay";
import { TermRow } from "../term/term-row";

export interface ChatBlockPlugin<$Block extends HTMLElement = HTMLElement> {
  type: string;
  makeBlock(): $Block;
  bindBlock(
    $block: $Block,
    content: ChatContentBlock,
    isStreaming: boolean,
    context?: ChatBlockContext,
  ): void;
  /** Paint an incremental chunk after the kind reducer has updated canonical
   * data. This must not reduce the model again. Rebind/finalize read that model. */
  appendDelta($block: $Block, delta: string): void;
  finalizeBlock($block: $Block): void;
  onRecycle($block: $Block): void;
}

/**
 * Stock defaults, theme grammar and explicit host overrides have separate layers.
 *
 * The BASE layer holds the thread's stock renderers. The HOST layer holds
 * explicit contributions through `ChatThread.registerPlugin/registerKind`
 * and takes precedence over a look. The THEME layer holds the
 * bound look's grammar, and `installTheme` replaces it whole — so removing a
 * theme is `installTheme([])`, and no key arithmetic anywhere can reach the
 * base.
 *
 * That separation is the point, and it is why this is not one map with a
 * `delete`. With one map, a look that answered a type the thread already
 * answers — `tool_call`, `markdown`, any of the ten the thread registers in
 * its constructor — took that type WITH IT on the way out: the delete removed
 * the key, not the look's contribution, and the core renderer never came
 * back. The type then fell through to the plain-text fallback, which ignores
 * every block that is not text, so it rendered empty. A host's own override
 * of a stock type was lost the same way, on a swap it had nothing to do with.
 *
 * No shipped theme collides with a core type today, so nothing rendered
 * wrong. The fault was reachable only through the extension this seam exists
 * to invite, which is the worst place to leave one.
 */
export class ChatPluginManager {
  /** Stock defaults; a theme may replace these while it is bound. */
  private base: Map<string, ChatBlockPlugin> = new Map();
  /** Explicit host contributions win over theme grammar and stock defaults. */
  private host: Map<string, ChatBlockPlugin> = new Map();
  /** The bound theme's grammar. Replaced whole, never edited by key. */
  private theme: Map<string, ChatBlockPlugin> = new Map();
  readonly disclosure = new Map<string, boolean>();

  constructor(readonly fallback: ChatBlockPlugin) {}

  register(plugin: ChatBlockPlugin): this {
    this.base.set(plugin.type, plugin);
    return this;
  }

  registerHost(plugin: ChatBlockPlugin): this {
    this.host.set(plugin.type, plugin);
    return this;
  }

  /**
   * Bind a theme's grammar, replacing whatever the last theme installed.
   *
   * Total rather than incremental: the caller says what the theme speaks and
   * the manager owns the transition, so there is no withdraw step to forget,
   * and no record of "what I installed last time" that has to stay in sync
   * with a second `plugins()` call. `installTheme([])` removes a theme.
   */
  installTheme(plugins: readonly ChatBlockPlugin[]): this {
    this.theme = new Map(plugins.map((plugin) => [plugin.type, plugin]));
    return this;
  }

  /** Host contributions address canonical block kinds even when a look routes
   * them onto a dialect. A host's explicit dialect override is most specific. */
  resolve(blockType: string, routedType = blockType): ChatBlockPlugin {
    return this.host.get(routedType) ?? this.host.get(blockType) ??
      this.theme.get(routedType) ?? this.base.get(routedType) ?? this.fallback;
  }

  getPlugin(type: string): ChatBlockPlugin {
    return this.resolve(type);
  }
}

/** A RUNNING tool call breathes. Status colour is a fact about the outcome;
 * the pulse is the "still going" affordance every CLI shows while one is in
 * flight, and it must stop the moment the call settles. Web Animations
 * rather than a keyframes rule because blocks are built inline in the
 * bubble's light DOM, where no plugin stylesheet exists to hold keyframes;
 * the WeakMap keys the running animation to the element so rebinds are
 * idempotent and a recycled block can always be silenced. Reduced motion is
 * honoured here (WAAPI does not read the media query on its own). */
const RUNNING_PULSES = new WeakMap<HTMLElement, Animation>();

export function setRunningPulse($el: HTMLElement, running: boolean): void {
  const active = RUNNING_PULSES.get($el);
  if (!running) {
    if (active != null) {
      active.cancel();
      RUNNING_PULSES.delete($el);
    }
    return;
  }
  if (active != null) return;
  if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  RUNNING_PULSES.set(
    $el,
    $el.animate([{ opacity: 1 }, { opacity: 0.35 }, { opacity: 1 }], {
      duration: 1200,
      iterations: Infinity,
      easing: "ease-in-out",
    }),
  );
}

export function isRunningStatus(status: Option<string>): boolean {
  return status != null && status !== "completed" && status !== "failed";
}

/** Index-bearing interior decoration slot; never an error or a visible row. */
export class QuietGapBlockPlugin implements ChatBlockPlugin<HTMLElement> {
  type = "quiet_gap";
  makeBlock(): HTMLElement {
    const element = document.createElement("span");
    element.style.display = "none";
    element.hidden = true;
    element.setAttribute("aria-hidden", "true");
    return element;
  }
  bindBlock(): void {}
  appendDelta(): void {}
  finalizeBlock(): void {}
  onRecycle(): void {}
}

export class LoadingBlockPlugin implements ChatBlockPlugin<ChatLoadingDots> {
  type = "loading";

  makeBlock(): ChatLoadingDots {
    return ChatLoadingDots.makeWith();
  }

  bindBlock(): void {}
  appendDelta(): void {}
  finalizeBlock(): void {}
  onRecycle(): void {}
}

/**
 * Content that IS code, rendered as a code panel: `std-code` (CodeMirror,
 * read-only) inside a themed frame, so a lisp drop or a pasted script gets
 * syntax highlighting instead of prose typography. The frame takes
 * --chat-code-* hooks first and falls back to the terminal chain — a look
 * that themed its terminal has themed this panel; a bubble look that wants
 * the BUBBLE to be the panel sets --chat-code-bg: transparent on its
 * `std-chat-bubble[code]` rule (the bubble stamps that attribute when a
 * message's whole content is code). Highlight colours ride the --fg-syntax-*
 * tokens std-code already consumes, overridable per look.
 */
export class CodeBlockPlugin implements ChatBlockPlugin<HTMLElement> {
  type = "code";

  /** The renderer is read at render time, not construction time, so a host
   * that installs `tentoUiRenderers()` after the thread was made still gets
   * it on the next bind. */
  constructor(private renderers: () => ChatRenderers = Renderers.current) {}

  /**
   * The panel's hover affordance, stated once and composed into every
   * register's turn $styles — the panel itself is built with inline
   * styles, and :hover cannot be stated inline. The wash is an inset
   * shadow: it paints above the ground, under the text, and follows the
   * panel's own rounded corners in whatever radius the look declared.
   * currentColor is the panel's ink, so the same 7% reads on the default
   * dark ground and on a look's light one alike; --chat-code-hover
   * retunes it per look. The arrow cursor is part of the same state —
   * the panel is a fragment you hover, not a document you caret.
   */
  static $panelStyle: Style.Compat = css`
    [code-panel] {
      cursor: default;
      transition: box-shadow 0.15s ease;
    }

    [code-panel]:hover {
      box-shadow: inset 0 0 0 999px
        var(--chat-code-hover, color-mix(in srgb, currentColor 7%, transparent));
    }
  `;

  private state = new WeakMap<
    HTMLElement,
    { text: string; language: string; gen: number }
  >();

  makeBlock(): HTMLElement {
    const $panel = document.createElement("div");
    $panel.toggleAttribute("code-panel", true);
    $panel.style.background =
      "var(--chat-code-bg, var(--chat-terminal-bg, #1a1a1a))";
    $panel.style.color = "var(--chat-code-fg, var(--chat-terminal-fg, #e6e6e6))";
    $panel.style.borderRadius = "var(--chat-terminal-radius, 8px)";
    $panel.style.border = "var(--chat-terminal-edge, none)";
    $panel.style.padding = "var(--chat-code-padding, 2px 4px)";
    $panel.style.overflowX = "auto";
    // What goes INSIDE the panel is the code renderer's — std-code with a
    // plain-text understudy when the tento-ui renderers are installed, a
    // <pre> otherwise (render/code.ts). The panel only owns the frame.
    return $panel;
  }

  bindBlock($block: HTMLElement, content: ChatContentBlock): void {
    if (content.type !== "code") return;
    this.state.set($block, {
      text: content.text,
      language: content.language,
      gen: 0,
    });
    this.render($block);
  }

  private render($block: HTMLElement): void {
    const s = this.state.get($block);
    if (!s) return;
    s.gen++;
    void this.renderers().code.render($block, { text: s.text, language: s.language });
  }

  appendDelta($block: HTMLElement, delta: string): void {
    const s = this.state.get($block);
    if (!s) return;
    s.text += delta;
    this.render($block);
  }

  finalizeBlock(): void {}

  onRecycle($block: HTMLElement): void {
    this.state.delete($block);
  }
}

export class MarkdownBlockPlugin implements ChatBlockPlugin<HTMLElement> {
  type = "markdown";
  private accumulator = new WeakMap<HTMLElement, string>();

  constructor(private renderers: () => ChatRenderers = Renderers.current) {}

  makeBlock(): HTMLElement {
    const $wrapper = document.createElement("div");
    const $p = document.createElement("p");
    $p.style.margin = "0";
    $p.style.whiteSpace = "pre-wrap";
    $p.style.wordBreak = "break-word";
    $wrapper.appendChild($p);
    return $wrapper;
  }

  bindBlock(
    $block: HTMLElement,
    content: ChatContentBlock,
    isStreaming: boolean,
  ): void {
    if (content.type !== "text") return;
    this.accumulator.set($block, content.text);
    if (!isStreaming) {
      void this.renderers().prose.render($block, content.text);
    } else {
      const $p = $block.firstElementChild as HTMLParagraphElement;
      if ($p) $p.textContent = content.text;
    }
  }

  appendDelta($block: HTMLElement, delta: string): void {
    const current = this.accumulator.get($block) ?? "";
    const updated = current + delta;
    this.accumulator.set($block, updated);
    const $p = $block.firstElementChild as HTMLParagraphElement;
    if ($p) $p.textContent = updated;
  }

  finalizeBlock($block: HTMLElement): void {
    const text = this.accumulator.get($block) ?? "";
    void this.renderers().prose.render($block, text);
  }

  onRecycle($block: HTMLElement): void {
    this.accumulator.delete($block);
    DomUtils.clearChildren($block);
    const $p = document.createElement("p");
    $p.style.margin = "0";
    $p.style.whiteSpace = "pre-wrap";
    $p.style.wordBreak = "break-word";
    $block.appendChild($p);
  }
}

export class InterruptedBlockPlugin
  implements ChatBlockPlugin<HTMLParagraphElement>
{
  type = "interrupted";

  makeBlock(): HTMLParagraphElement {
    const $p = document.createElement("p");
    $p.style.margin = "0";
    $p.style.fontStyle = "italic";
    $p.style.color = "var(--text-ter)";
    $p.textContent = "Interrupted";
    return $p;
  }

  bindBlock($block: HTMLParagraphElement): void {
    $block.textContent = "Interrupted";
  }
  appendDelta(): void {}
  finalizeBlock(): void {}
  onRecycle($block: HTMLParagraphElement): void {
    $block.textContent = "";
  }
}

/** Unregistered kinds remain inspectable instead of silently disappearing. */
export class UnknownBlockPlugin implements ChatBlockPlugin<HTMLPreElement> {
  type = "unknown";
  private bound = new WeakMap<HTMLPreElement, ChatContentBlock>();
  makeBlock(): HTMLPreElement {
    const element = document.createElement("pre");
    element.style.whiteSpace = "pre-wrap";
    element.style.overflowWrap = "anywhere";
    return element;
  }
  bindBlock(element: HTMLPreElement, content: ChatContentBlock): void {
    this.bound.set(element, content);
    let payload: string;
    try { payload = JSON.stringify(content, null, 2); }
    catch { payload = String(content); }
    element.textContent = `${content.type}\n${payload}`;
  }
  appendDelta(element: HTMLPreElement, _delta: string): void {
    const content = this.bound.get(element);
    if (content) this.bindBlock(element, content);
  }
  finalizeBlock(element: HTMLPreElement): void {
    const content = this.bound.get(element);
    if (content) this.bindBlock(element, content);
  }
  onRecycle(element: HTMLPreElement): void {
    this.bound.delete(element);
    element.textContent = "";
  }
}

export class TextBlockPlugin implements ChatBlockPlugin<HTMLParagraphElement> {
  type = "text";

  makeBlock(): HTMLParagraphElement {
    const $p = document.createElement("p");
    $p.style.margin = "0";
    $p.style.whiteSpace = "pre-wrap";
    $p.style.wordBreak = "break-word";
    return $p;
  }

  bindBlock(
    $block: HTMLParagraphElement,
    content: ChatContentBlock,
    _isStreaming: boolean,
  ): void {
    if (content.type !== "text") return;
    $block.textContent = content.text;
  }

  appendDelta($block: HTMLParagraphElement, delta: string): void {
    $block.textContent += delta;
  }

  finalizeBlock(_$block: HTMLParagraphElement): void {}

  onRecycle($block: HTMLParagraphElement): void {
    $block.textContent = "";
  }
}

export class ErrorBlockPlugin implements ChatBlockPlugin<HTMLElement> {
  type = "error";

  makeBlock(): HTMLElement {
    const $container = document.createElement("div");
    $container.style.background = "var(--red-dimmed)";
    $container.style.borderRadius = "8px";
    $container.style.padding = "12px";
    const $text = Typography.p("").assignStyles({
      color: "var(--text-sec)",
      "--margin-top": "0px",
      "--margin-bottom": "0px",
    });
    $container.appendChild($text);
    return $container;
  }

  bindBlock(
    $block: HTMLElement,
    content: ChatContentBlock,
  ): void {
    if (content.type !== "error") return;
    const $text = $block.firstElementChild as HTMLElement;
    if ($text) $text.textContent = content.message;
  }

  appendDelta(): void {}
  finalizeBlock(): void {}

  onRecycle($block: HTMLElement): void {
    const $text = $block.firstElementChild as HTMLElement;
    if ($text) $text.textContent = "";
  }
}

/**
 * One tool invocation, condensed — the grammar every native agent surface
 * converged on (Cursor's collapsed command cards, ChatGPT's activity chips,
 * Codex's one-line entries with "+N lines"): a single header row carrying
 * the command, a dim line-count hint, and how it ended; the output paints
 * only on demand, as term rows, and the entry collapses back the same way.
 *
 * Expansion is keyed on the content-block OBJECT, not the element: the
 * thread's recycler rebuilds block elements from values whenever a turn
 * scrolls back in, and per-element state would silently re-collapse every
 * entry the reader had opened. The block object lives in the message list
 * and is stable for the conversation's life.
 *
 * Chrome rides the terminal hooks (--chat-terminal-font/size/bg/fg/radius)
 * plus --chat-toolcall-padding, so a look that already styles its terminal
 * has already styled this card.
 */
export class ToolCallBlockPlugin implements ChatBlockPlugin<HTMLElement> {
  type = "tool_call";
  private expanded = new ChatBlockDisclosure();
  private state = new WeakMap<
    HTMLElement,
    {
      block: {
        tool: Option<string>;
        command: string;
        output: Option<string>;
        status: Option<string>;
      };
      $chevron: HTMLElement;
      $command: HTMLElement;
      $hint: HTMLElement;
      $status: HTMLElement;
      $output: HTMLElement;
    }
  >();

  makeBlock(): HTMLElement {
    const $root = document.createElement("div");
    // Every property takes a --chat-toolcall-* hook FIRST, falling back to
    // the terminal chain: a look that styles its terminal has styled this
    // card, and a look that wants the card as an ADMIN MESSAGE — no ground
    // of its own, lighter, smaller (iMessage) — overrides just the card.
    $root.style.fontFamily = "var(--chat-terminal-font, ui-monospace, monospace)";
    $root.style.fontSize =
      "var(--chat-toolcall-size, var(--chat-terminal-size, 0.9em))";
    $root.style.color =
      "var(--chat-toolcall-ink, var(--chat-terminal-fg, inherit))";
    $root.style.background =
      "var(--chat-toolcall-bg, var(--chat-terminal-bg, color-mix(in srgb, currentColor 6%, transparent)))";
    $root.style.borderRadius = "var(--chat-terminal-radius, 8px)";
    $root.style.border = "var(--chat-terminal-edge, none)";
    $root.style.padding = "var(--chat-toolcall-padding, 6px 10px)";

    const $header = document.createElement("div");
    $header.style.display = "flex";
    $header.style.alignItems = "baseline";
    $header.style.gap = "8px";
    $header.style.minWidth = "0";

    // The chevron is the house SVG, rotated by transform so the flip
    // ANIMATES — the storybook expander's own pattern (angle-right at rest,
    // 90deg when open). A text glyph swap cannot animate.
    const $chevron = document.createElement("span");
    $chevron.style.flex = "none";
    $chevron.style.display = "flex";
    $chevron.style.alignItems = "center";
    $chevron.style.alignSelf = "center";
    $chevron.style.opacity = "0.55";
    $chevron.style.transition = "transform 0.18s ease";
    const $angle = LonaIcons.$svg(AngleRightIcon);
    $angle.style.width = "11px";
    $angle.style.height = "11px";
    $angle.style.display = "block";
    $chevron.appendChild($angle);
    const $command = document.createElement("span");
    $command.style.flex = "0 1 auto";
    $command.style.whiteSpace = "nowrap";
    $command.style.overflow = "hidden";
    $command.style.textOverflow = "ellipsis";
    const $hint = document.createElement("span");
    $hint.style.flex = "none";
    $hint.style.opacity = "0.55";
    const $status = document.createElement("span");
    $status.style.flex = "none";
    $status.style.marginLeft = "auto";

    $header.append($chevron, $command, $hint, $status);

    // The expanded output is a SUB-BUBBLE, and its geometry is an
    // alignment claim: the header's text starts at chevron (11px) + gap
    // (8px), so the default margin-left 9px + own padding-left 10px puts
    // the output's first column exactly under the "Bash(" text — the
    // three constants move together. A PANELED look (chatgpt) zeroes the
    // inset and the radius instead, so its divider runs the card's full
    // width, square. The ground defaults to a tenth of the card's ink,
    // which reads as a quiet but present panel on every look;
    // --chat-toolcall-output-* overrides it.
    const $output = document.createElement("div");
    $output.style.display = "none";
    $output.style.whiteSpace = "var(--chat-terminal-flow, pre-wrap)";
    $output.style.wordBreak = "var(--chat-terminal-break, break-all)";
    $output.style.marginTop = "4px";
    $output.style.marginLeft = "var(--chat-toolcall-output-inset, 9px)";
    $output.style.padding = "6px 10px";
    $output.style.background =
      "var(--chat-toolcall-output-bg, color-mix(in srgb, currentColor 10%, transparent))";
    $output.style.borderRadius = "var(--chat-toolcall-output-radius, 8px)";
    // A look that wants no panel at all separates header from output with
    // a rule instead (chatgpt's grammar): zero the grounds, state a
    // divider.
    $output.style.borderTop = "var(--chat-toolcall-divider, none)";
    $output.style.opacity = "0.85";

    $root.append($header, $output);

    GESTURE_MANAGER.addPointerEvent($header, {
      onClick: () => {
        const s = this.state.get($root);
        if (!s || s.block.output == null || s.block.output.length === 0) return;
        this.expanded.set(s.block, !(this.expanded.get(s.block) ?? false));
        this.render($root);
      },
      allowTextSelection: true,
    });
    return $root;
  }

  bindBlock($block: HTMLElement, content: ChatContentBlock, _streaming: boolean, context?: ChatBlockContext): void {
    if (content.type !== "tool_call") return;
    this.expanded.bind(content, context);
    const [$header] = $block.children;
    const [$chevron, $command, $hint, $status] = ($header as HTMLElement)
      .children as unknown as HTMLElement[];
    const $output = $block.children[1] as HTMLElement;
    this.state.set($block, {
      block: content,
      $chevron,
      $command,
      $hint,
      $status,
      $output,
    });
    this.render($block);
  }

  private render($block: HTMLElement): void {
    const s = this.state.get($block);
    if (!s) return;
    const { block } = s;
    const outputLines =
      block.output == null || block.output.length === 0
        ? []
        : block.output.split("\n");
    const isOpen = outputLines.length > 0 && (this.expanded.get(block) ?? false);

    // Hidden (not removed) when there is nothing to expand, so the command
    // column stays aligned across a run of chips.
    s.$chevron.style.visibility = outputLines.length === 0 ? "hidden" : "visible";
    s.$chevron.style.transform = isOpen ? "rotate(90deg)" : "rotate(0deg)";
    ((s.$chevron.parentElement as HTMLElement).style.cursor =
      outputLines.length === 0 ? "default" : "pointer");
    s.$command.textContent = this.headerLabel(block);
    s.$hint.textContent = isOpen || outputLines.length === 0 ? "" : `+${outputLines.length} lines`;
    s.$status.textContent =
      block.status === "failed" ? "✗" : block.status == null || block.status === "completed" ? "✓" : "·";
    s.$status.style.color =
      block.status === "failed" ? "var(--red, #e5484d)" : "inherit";
    s.$status.style.opacity = block.status === "failed" ? "1" : "0.45";
    setRunningPulse(s.$status, isRunningStatus(block.status));

    DomUtils.clearChildren(s.$output);
    if (isOpen) {
      // Expanding reveals the EXACT invocation — the header's readable
      // label is a rendering, the raw command is the record.
      const rest = block.command.split("\n");
      for (const raw of [...rest, ...outputLines]) {
        const $row = TermRow.$row(TermRow.parse(raw));
        if (TermRow.parse(raw).text.trim().length === 0) {
          $row.style.minHeight = "1lh";
        }
        s.$output.appendChild($row);
      }
    }
    s.$output.style.display = isOpen ? "block" : "none";
  }

  /**
   * The condensed header, Claude Code's own idiom: `Bash(inner command)`.
   * A raw invocation is a wrapper around what the reader cares about —
   * every harness command ships as `/bin/zsh -lc '…'` — so the label names
   * the tool and shows the wrapper's PAYLOAD, one quote layer stripped.
   * Presentation only: expanding paints the exact raw command.
   */
  private headerLabel(block: {
    tool: Option<string>;
    command: string;
  }): string {
    const name =
      block.tool == null || block.tool === "commandExecution"
        ? "Bash"
        : block.tool;
    let inner = block.command;
    const m = inner.match(/^\s*(?:\/bin\/)?(?:zsh|bash|sh)\s+-l?c\s+([\s\S]+)$/);
    if (m) {
      inner = m[1].trim();
      const q = inner[0];
      if ((q === "'" || q === '"') && inner.length >= 2 && inner.endsWith(q)) {
        inner = inner.slice(1, -1);
      }
    }
    return `${name}(${inner.split("\n")[0]})`;
  }

  appendDelta($block: HTMLElement): void {
    // Render-only: the thread already canonicalized the delta into the
    // model, and `state.block` IS the model object — appending here again
    // would double every streamed chunk.
    this.render($block);
  }

  finalizeBlock(): void {}

  onRecycle($block: HTMLElement): void {
    const s = this.state.get($block);
    if (s) setRunningPulse(s.$status, false);
    this.state.delete($block);
  }
}



/**
 * Terminal output in a chat thread: rows of finished ANSI, painted through
 * the same term-row primitive the session replay and std-term-screen use, so
 * a terminal block in chat and a terminal figure on a page cannot drift.
 *
 * Two presentations, one block. A still (`replay: false`) paints all rows at
 * bind and appends live ones as deltas arrive; a replay (`replay: true`)
 * mounts std-session-replay and plays the rows in on its beat mechanics.
 *
 * Recycler discipline is the load-bearing part. The thread's ScrollVertical
 * recycles bubbles aggressively, and `applyDelta` writes into on-screen
 * blocks directly while off-screen ones only get the model — so the block
 * value (`lines`) is the sole source of truth and `bindBlock` reconstructs
 * everything from it. `onRecycle` recycles the replay child, which stops its
 * timer and observer; a pooled component holding a pending timeout writes
 * into a detached screen, a leak this codebase has paid for twice.
 */
export class TerminalBlockPlugin implements ChatBlockPlugin<HTMLElement> {
  type = "terminal";
  private state = new WeakMap<
    HTMLElement,
    { lines: string[]; $replay: Option<SessionReplay> }
  >();

  makeBlock(): HTMLElement {
    const $screen = document.createElement("div");
    // The same code-fragment chrome the code panel carries: the shared
    // $panelStyle hover wash follows whatever radius the look declared
    // (a bubble look's rounded diff panel; a tui look's square flow).
    $screen.toggleAttribute("code-panel", true);
    // The row grid, in miniature. Long rows WRAP, breaking anywhere — which
    // is what a real terminal does at its column edge — rather than growing
    // a horizontal scrollbar, which no terminal has ever drawn. The grid
    // survives wrapping because every visual line still takes exactly one
    // line box: with an integral line-height (the tui looks set
    // --chat-terminal-leading to a whole-pixel round()), a k-line wrap is
    // exactly k lh, and row heights stay on the grid. Everything is a
    // custom property with an inert default, because custom properties
    // inherit through shadow roots: the page hosting the thread paints the
    // terminal's ground and face once, and every terminal block follows —
    // including a look that genuinely wants captured columns to overflow
    // instead of wrap (--chat-terminal-flow: pre plus an overflow-x).
    $screen.style.whiteSpace = "var(--chat-terminal-flow, pre-wrap)";
    $screen.style.wordBreak = "var(--chat-terminal-break, break-all)";
    $screen.style.overflowX = "var(--chat-terminal-overflow-x, visible)";
    $screen.style.fontFamily =
      "var(--chat-terminal-font, ui-monospace, monospace)";
    $screen.style.fontSize = "var(--chat-terminal-size, inherit)";
    $screen.style.lineHeight = "var(--chat-terminal-leading, inherit)";
    $screen.style.background = "var(--chat-terminal-bg, transparent)";
    $screen.style.color = "var(--chat-terminal-fg, inherit)";
    $screen.style.padding = "var(--chat-terminal-padding, 0)";
    $screen.style.borderRadius = "var(--chat-terminal-radius, 0)";
    $screen.style.border = "var(--chat-terminal-edge, none)";
    return $screen;
  }

  bindBlock($block: HTMLElement, content: ChatContentBlock): void {
    if (content.type !== "terminal") return;
    const lines = content.lines.slice();

    if (content.replay) {
      const $replay = SessionReplay.make() as SessionReplay;
      $replay.bind({
        sample: lines.join("\n"),
        $line: null,
        timing: null,
        prompt: null,
      });
      $block.replaceChildren($replay);
      this.state.set($block, { lines, $replay });
      return;
    }

    $block.replaceChildren(...lines.map((raw) => this.$terminalRow(raw)));
    this.state.set($block, { lines, $replay: null });
  }

  /**
   * One painted row that always holds its line box. An empty div is zero
   * pixels tall, so without the floor every blank row a program printed
   * collapses and the transcript closes up wherever its author left air —
   * the same fix the session replay carries as its `.blank` rule.
   */
  private $terminalRow(raw: string): HTMLElement {
    const row = TermRow.parse(raw);
    const $row = TermRow.$row(row);
    if (row.text.trim().length === 0) $row.style.minHeight = "1lh";
    return $row;
  }

  appendDelta($block: HTMLElement, delta: string): void {
    const state = this.state.get($block);
    if (!state) return;
    const before = state.lines;
    const after = ChatTerminalLines.appendChunk(before, delta);
    state.lines = after;

    if (state.$replay) {
      // A replay mid-stream re-binds: the engine owns its own scheduling and
      // an append API on it would be a second engine. Streaming wants the
      // still form; this keeps the replay correct rather than fast.
      state.$replay.bind({
        sample: after.join("\n"),
        $line: null,
        timing: null,
        prompt: null,
      });
      return;
    }

    // Only the previously-open row and anything after it changed — repaint
    // from there, so a delta costs two rows on a two-thousand-row tail.
    const from = ChatTerminalLines.changedFrom(before, after);
    while ($block.children.length > from) {
      $block.lastElementChild?.remove();
    }
    for (let i = from; i < after.length; i++) {
      $block.appendChild(this.$terminalRow(after[i]));
    }
  }

  finalizeBlock(): void {}

  onRecycle($block: HTMLElement): void {
    const state = this.state.get($block);
    state?.$replay?.onRecycle();
    this.state.delete($block);
    $block.replaceChildren();
  }
}
